/**
 * Iter 67dc — Mobile + tablet hero search (redesign).
 *
 * Renders below `lg` (1024px). The desktop stepper (`HeroStepperSearch`) is
 * hidden in that breakpoint and this vertical card takes over.
 *
 * Layout (per product spec):
 *   ── slogan strip ───────────────────────────────────────────────
 *   "From Tirana to the Balkans · No Credit Card · Low Deposit · No Hidden Costs"
 *
 *   ── search card ───────────────────────────────────────────────
 *   1. Pick-up location  (autocomplete after 2-3 letters)
 *   2. Pick-up date · Pick-up time   (one row, two columns)
 *   3. Drop-off location (autocomplete after 2-3 letters)
 *   4. Drop-off date · Drop-off time (one row, two columns)
 *   5. Driver-aged checkbox (default ON = 25-65, OFF reveals age dropdown)
 *   6. Search button (full-width)
 *
 *   ── quick filters ─────────────────────────────────────────────
 *   No deposit · No credit card · Full protection · Automatic · SUV
 *   · 7 seats · Airport pickup   (chips wrap onto multiple lines)
 *
 * Times are displayed in 12-hour AM/PM (e.g. "10:00 AM") but stored
 * internally and submitted as 24-hour ("10:00") to keep URL params /
 * existing booking flows untouched.
 *
 * Reuses the centralised `/api/locations/catalog` autocomplete + the
 * `_nextLongWeekend()` smart-default helper from previous iterations.
 * `localStorage` persistence (`rentalcar_last_search_v1`) is preserved
 * so returning visitors keep their previous pick.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  MapPin, Calendar as CalendarIcon, Clock, UserCheck, Search, Loader2,
  CreditCard, ShieldCheck, Cog, Car, Users, Plane, Wallet,
  SlidersHorizontal, ChevronDown, ChevronRight,
} from "lucide-react";

// Balkan whitelist + Greece (per spec). Used to filter the cross-country
// `/locations/catalog` autocomplete down to the markets we want to surface.
const BALKAN_COUNTRIES = new Set([
  "Albania", "Bosnia and Herzegovina", "Bulgaria", "Croatia", "Greece",
  "Kosovo", "Montenegro", "North Macedonia", "Romania", "Serbia", "Slovenia",
]);

// 24-hour value (URL/storage) → 12-hour display (UI only).
const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      out.push(`${hh}:${mm}`);
    }
  }
  return out;
})();

function _to12h(value) {
  if (!value) return "";
  const [hStr, mStr] = value.split(":");
  let h = parseInt(hStr, 10);
  const m = mStr;
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${m} ${period}`;
}

const AGE_OPTIONS = [
  { value: "21", label: "18–24 (young driver)" },
  { value: "26", label: "25–29" },
  { value: "30", label: "30–65" },
  { value: "66", label: "66+ (senior)" },
];
const DEFAULT_AGE = "30"; // checkbox-checked baseline → 25-65 standard window

// Quick-filter chips. `params` is merged straight into the /cars URL.
// `quick` key maps to QuickFilterRail's preset IDs where applicable so the
// landing page lights up the matching chip + filters client-side.
const QUICK_FILTERS = [
  { id: "no_deposit",     label: "No deposit",     icon: Wallet,       params: { quick: "no_deposit" } },
  { id: "no_credit_card", label: "No credit card", icon: CreditCard,   params: { quick: "no_credit_card" } },
  { id: "full_insurance", label: "Full protection", icon: ShieldCheck, params: { quick: "full_insurance" } },
  { id: "automatic",      label: "Automatic",      icon: Cog,          params: { transmission: "Automatic" } },
  { id: "suv",            label: "SUV",            icon: Car,          params: { quick: "suv" } },
  { id: "seven_seats",    label: "7 seats",        icon: Users,        params: { seats: "7" } },
  { id: "airport_pickup", label: "Airport pickup", icon: Plane,        params: { pickup_location: "Airport" } },
];

// Short one-liner hints rendered under each filter in the dropdown.
const QUICK_FILTER_HINTS = {
  no_deposit:     "Reserve without locking funds on your card",
  no_credit_card: "Pay cash or debit at pickup",
  full_insurance: "Zero excess · all risks covered",
  automatic:      "Automatic gearbox only",
  suv:            "Larger boot · higher driving position",
  seven_seats:    "MPVs and 7-seat SUVs",
  airport_pickup: "Pickup at any airport terminal",
};

// --- date helpers (mirror iter 67cr's helper from HeroStepperSearch) ----------
function _nextLongWeekend() {
  const now = new Date();
  const day = now.getDay();
  const daysToFri = (5 - day + 7) % 7;
  const start = new Date(now);
  start.setDate(start.getDate() + daysToFri);
  start.setHours(10, 0, 0, 0);
  if (start.getTime() - now.getTime() < 24 * 3600 * 1000) {
    start.setDate(start.getDate() + 7);
  }
  const end = new Date(start);
  end.setDate(end.getDate() + 3);
  end.setHours(10, 0, 0, 0);
  return { start, end };
}
function _fmtDate(d) {
  if (!d) return "";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "2-digit" });
}
function _toIsoDate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// localStorage key for the renter's last search.
const LS_KEY = "rentalcar_last_search_v1";
function _loadLast() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || !v.saved_at) return null;
    const ageDays = (Date.now() - v.saved_at) / 86_400_000;
    if (ageDays > 60) return null;
    return v;
  } catch {
    return null;
  }
}
function _saveLast(v) {
  try { localStorage.setItem(LS_KEY, JSON.stringify({ ...v, saved_at: Date.now() })); } catch { /* ignore */ }
}

export default function HeroMobileSearch() {
  const navigate = useNavigate();
  const seed = useMemo(_nextLongWeekend, []);
  const last = useMemo(_loadLast, []);
  const [pickupLoc, setPickupLoc] = useState(last?.pickupLoc || "");
  const [dropoffLoc, setDropoffLoc] = useState(last?.dropoffLoc || "");
  const [pickupDate, setPickupDate] = useState(() => {
    const isoStart = last?.pickupDate;
    if (isoStart) {
      const d = new Date(isoStart);
      if (d.getTime() >= Date.now() - 86_400_000) return d;
    }
    return seed.start;
  });
  const [dropoffDate, setDropoffDate] = useState(() => {
    const isoEnd = last?.dropoffDate;
    if (isoEnd) {
      const d = new Date(isoEnd);
      if (d.getTime() >= Date.now() - 86_400_000) return d;
    }
    return seed.end;
  });
  const [pickupTime, setPickupTime] = useState(last?.pickupTime || "10:00");
  const [dropoffTime, setDropoffTime] = useState(last?.dropoffTime || "10:00");
  // `standardAge` = renter is in the 25-65 default insurance window.
  // Checked by default → no age dropdown shown. Unchecked → reveal dropdown.
  const [standardAge, setStandardAge] = useState(last?.standardAge ?? true);
  const [age, setAge] = useState(last?.age || DEFAULT_AGE);
  const [submitting, setSubmitting] = useState(false);
  const [pickupCalOpen, setPickupCalOpen] = useState(false);
  const [dropoffCalOpen, setDropoffCalOpen] = useState(false);

  const onSearch = (extraParams = {}) => {
    if (!pickupLoc.trim()) return;
    setSubmitting(true);
    _saveLast({
      pickupLoc: pickupLoc.trim(),
      dropoffLoc: dropoffLoc.trim(),
      pickupDate: pickupDate.toISOString(),
      dropoffDate: dropoffDate.toISOString(),
      pickupTime, dropoffTime,
      standardAge, age,
    });
    const sp = new URLSearchParams();
    sp.set("pickup_location", pickupLoc.trim());
    sp.set("dropoff_location", (dropoffLoc.trim() || pickupLoc.trim()));
    sp.set("start_date", `${_toIsoDate(pickupDate)}T${pickupTime}`);
    sp.set("end_date",   `${_toIsoDate(dropoffDate)}T${dropoffTime}`);
    sp.set("driver_age", standardAge ? DEFAULT_AGE : age);
    Object.entries(extraParams).forEach(([k, v]) => { if (v != null && v !== "") sp.set(k, String(v)); });
    navigate(`/cars?${sp.toString()}`);
  };

  const canSearch = pickupLoc.trim().length > 0 && pickupDate && dropoffDate;

  return (
    <div className="lg:hidden" data-testid="hero-mobile-search">
      {/* ── Search card ───────────────────────────────────────────── */}
      {/* Iter 67dq — amber/black palette matching the RentalCar.al logo.
          Iter 68h — Removed the "From Tirana to the Balkans · No Credit Card …"
          slogan strip; the same trust signals now live in HeroTrustPills
          (mobile light variant) right above this card. */}
      <div className="bg-amber-500 rounded-3xl shadow-2xl ring-1 ring-amber-700/40 p-4 sm:p-5 space-y-3" data-testid="hero-mobile-search-card">
        {/* 1. Pick-up location */}
        <_LocationField
          testId="hero-mobile-pickup-loc"
          label="Pick-up location"
          value={pickupLoc}
          onChange={setPickupLoc}
          kind="pickup"
        />

        {/* 2. Pick-up date · Pick-up time (one row) */}
        <_DateTimeRow
          testIdPrefix="hero-mobile-pickup"
          dateLabel="Pick-up date"
          timeLabel="Pick-up time"
          date={pickupDate}
          onChangeDate={(d) => {
            if (!d) return;
            setPickupDate(d);
            setPickupCalOpen(false);
            if (dropoffDate && d.getTime() >= dropoffDate.getTime()) {
              const e = new Date(d);
              e.setDate(e.getDate() + 3);
              setDropoffDate(e);
            }
          }}
          calOpen={pickupCalOpen}
          setCalOpen={setPickupCalOpen}
          time={pickupTime}
          onChangeTime={setPickupTime}
        />

        {/* 3. Drop-off location */}
        <_LocationField
          testId="hero-mobile-dropoff-loc"
          label="Drop-off location"
          value={dropoffLoc}
          onChange={setDropoffLoc}
          kind="dropoff"
          rightHint={
            !dropoffLoc && pickupLoc
              ? (
                <button
                  type="button"
                  onClick={() => setDropoffLoc(pickupLoc)}
                  className="text-[10px] uppercase tracking-wider font-bold text-indigo-600 hover:text-indigo-800"
                  data-testid="hero-mobile-same-as-pickup"
                >
                  Same as pick-up
                </button>
              )
              : null
          }
        />

        {/* 4. Drop-off date · Drop-off time (one row) */}
        <_DateTimeRow
          testIdPrefix="hero-mobile-dropoff"
          dateLabel="Drop-off date"
          timeLabel="Drop-off time"
          date={dropoffDate}
          minDate={pickupDate}
          onChangeDate={(d) => {
            if (!d) return;
            setDropoffDate(d);
            setDropoffCalOpen(false);
          }}
          calOpen={dropoffCalOpen}
          setCalOpen={setDropoffCalOpen}
          time={dropoffTime}
          onChangeTime={setDropoffTime}
        />

        {/* 5. Driver-aged checkbox + conditional age dropdown */}
        <div className="rounded-2xl border border-neutral-200 bg-white px-3 py-2.5" data-testid="hero-mobile-driver-block">
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="hero-mobile-standard-age-label">
            <Checkbox
              checked={standardAge}
              onCheckedChange={(v) => setStandardAge(!!v)}
              data-testid="hero-mobile-standard-age"
            />
            <UserCheck className="h-4 w-4 text-violet-600" />
            <span className="text-sm font-semibold text-neutral-800">
              Driver aged 25–65
            </span>
            <span className="text-[11px] text-neutral-500 ml-1">standard rates</span>
          </label>
          {!standardAge && (
            <div className="mt-2.5 pt-2.5 border-t border-neutral-100" data-testid="hero-mobile-age-row">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-neutral-500 mb-1.5">
                Driver's age
              </div>
              <Select value={age} onValueChange={setAge}>
                <SelectTrigger className="rounded-xl h-10 text-sm" data-testid="hero-mobile-driver-age">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AGE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* 6. Search button */}
        <Button
          size="lg"
          onClick={() => onSearch()}
          disabled={!canSearch || submitting}
          className="w-full rounded-2xl h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold text-base shadow-lg shadow-blue-500/30"
          data-testid="hero-mobile-search-submit"
        >
          {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
          Search
        </Button>
      </div>

      {/* ── Quick filters dropdown ────────────────────────────────── */}
      <div className="mt-4" data-testid="hero-mobile-quick-filters">
        <_QuickFiltersDropdown
          enabled={canSearch && !submitting}
          onPick={(params) => onSearch(params)}
        />
      </div>
    </div>
  );
}

/* ---------- Filters dropdown ------------------------------------------- */

function _QuickFiltersDropdown({ enabled, onPick }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside-click so the panel feels native on mobile.
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, []);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={!enabled}
        className="w-full inline-flex items-center justify-between rounded-2xl bg-white/95 hover:bg-white px-4 py-3 ring-1 ring-neutral-200 hover:ring-amber-300 shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="hero-mobile-quick-filters-trigger"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="inline-flex items-center gap-2">
          <span className="h-7 w-7 rounded-lg bg-amber-50 flex items-center justify-center">
            <SlidersHorizontal className="h-3.5 w-3.5 text-amber-700" />
          </span>
          <span className="text-sm font-bold uppercase tracking-wider text-neutral-800">Filters</span>
          <span className="text-[11px] text-neutral-500 font-medium hidden sm:inline">7 quick presets</span>
        </span>
        <ChevronDown
          className={`h-4 w-4 text-neutral-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          className="absolute z-30 left-0 right-0 mt-2 rounded-2xl bg-white border border-neutral-200 shadow-2xl shadow-neutral-900/10 overflow-hidden"
          data-testid="hero-mobile-quick-filters-panel"
          role="menu"
        >
          <div className="px-4 py-2 border-b border-neutral-100 text-[10px] uppercase tracking-[0.16em] font-bold text-neutral-500">
            Tap to search with this filter
          </div>
          <ul className="py-1 max-h-[60vh] overflow-y-auto">
            {QUICK_FILTERS.map(({ id, label, icon: Icon, params }) => (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => { setOpen(false); onPick(params); }}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-amber-50 active:bg-amber-100 transition"
                  data-testid={`hero-mobile-quick-${id}`}
                  role="menuitem"
                >
                  <span className="h-9 w-9 rounded-xl bg-amber-50 ring-1 ring-amber-100 flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-amber-700" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-neutral-900">{label}</span>
                    <span className="block text-[11px] text-neutral-500">{QUICK_FILTER_HINTS[id]}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-neutral-300 shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ---------- Field components ------------------------------------------- */

function _LocationField({ testId, label, value, onChange, rightHint, kind = "pickup" }) {
  // Iter 69 — switched from `/api/locations/catalog` (full Balkan catalogue,
  // including cities WITHOUT cars) to `/api/locations/grouped` so the
  // dropdown surfaces only destinations with listed cars, grouped by
  // country with Albania pinned first.
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/locations/grouped", { params: { kind } });
        if (!cancelled) setGroups(r.data || []);
      } catch {
        if (!cancelled) setGroups([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kind]);

  // Close when tapping outside.
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, []);

  const needle = (value || "").trim().toLowerCase();
  // Build a filtered view of the grouped data — drop groups that have no
  // matching items after the renter starts typing.
  const filteredGroups = needle
    ? groups
        .map((g) => ({ ...g, items: g.items.filter((d) => d.toLowerCase().includes(needle)) }))
        .filter((g) => g.items.length > 0)
    : groups;
  const totalShown = filteredGroups.reduce((acc, g) => acc + g.items.length, 0);

  return (
    <div ref={wrapRef} className="rounded-2xl border border-neutral-200 bg-white px-3 py-2.5 relative" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <span className="h-5 w-5 rounded-md bg-sky-50 flex items-center justify-center">
          <MapPin className="h-3.5 w-3.5 text-sky-600" />
        </span>
        <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-neutral-500">{label}</span>
        {rightHint && <span className="ml-auto">{rightHint}</span>}
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={value}
          placeholder="Choose a city, airport or station"
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          className="border-0 px-0 h-7 text-sm font-semibold focus-visible:ring-0 placeholder:text-neutral-400 placeholder:font-normal flex-1"
          data-testid={`${testId}-input`}
          autoComplete="off"
        />
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o); }}
          onTouchStart={(e) => { e.preventDefault(); setOpen((o) => !o); }}
          className="h-7 w-7 flex items-center justify-center rounded-md text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition shrink-0"
          aria-label="Toggle destinations list"
          data-testid={`${testId}-chevron`}
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-2 z-30 max-h-80 overflow-y-auto rounded-2xl bg-white border border-neutral-200 shadow-xl py-1.5">
          {loading && groups.length === 0 ? (
            <div className="px-3 py-3 text-xs text-neutral-400 flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading destinations…
            </div>
          ) : totalShown === 0 ? (
            <div className="px-3 py-3 text-xs text-neutral-400" data-testid={`${testId}-empty`}>
              {groups.length === 0
                ? "No destinations available yet."
                : "No match — clear the box to see all destinations."}
            </div>
          ) : (
            filteredGroups.map((grp) => (
              <div key={grp.country}>
                <div className="sticky top-0 z-10 bg-white/95 backdrop-blur px-3 py-1.5 border-b border-neutral-100 flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-neutral-600">
                  {grp.flag && <span className="text-base leading-none">{grp.flag}</span>}
                  <span>{grp.country}</span>
                  <span className="ml-auto text-neutral-400 normal-case tracking-normal text-[11px] font-medium">{grp.items.length}</span>
                </div>
                {grp.items.map((dest) => (
                  <button
                    key={dest}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 flex items-center gap-2"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { onChange(dest); setOpen(false); }}
                    data-testid={`${testId}-suggest-${dest.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
                  >
                    <MapPin className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                    <span className="flex-1 min-w-0 truncate text-neutral-900">{dest}</span>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function _DateTimeRow({
  testIdPrefix, dateLabel, timeLabel,
  date, onChangeDate, calOpen, setCalOpen, minDate,
  time, onChangeTime,
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {/* Date — popover calendar (closes on selection — spec: smooth pop-up). */}
      <Popover open={calOpen} onOpenChange={setCalOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="rounded-2xl border border-neutral-200 bg-white px-3 py-2.5 text-left w-full hover:border-neutral-300 transition"
            data-testid={`${testIdPrefix}-date`}
          >
            <div className="flex items-center gap-2 mb-1">
              <CalendarIcon className="h-3.5 w-3.5 text-indigo-600" />
              <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-neutral-500">{dateLabel}</span>
            </div>
            <div className="text-sm font-semibold text-neutral-900">
              {_fmtDate(date) || "Choose date"}
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0 w-auto" data-testid={`${testIdPrefix}-date-popover`}>
          <Calendar
            mode="single"
            selected={date}
            onSelect={onChangeDate}
            disabled={(d) => {
              const today = new Date(); today.setHours(0, 0, 0, 0);
              if (d < today) return true;
              if (minDate) {
                const m = new Date(minDate); m.setHours(0, 0, 0, 0);
                if (d < m) return true;
              }
              return false;
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>

      {/* Time — 12h AM/PM display, 24h value */}
      <div className="rounded-2xl border border-neutral-200 bg-white px-3 py-2.5">
        <div className="flex items-center gap-2 mb-1">
          <Clock className="h-3.5 w-3.5 text-amber-600" />
          <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-neutral-500">{timeLabel}</span>
        </div>
        <Select value={time} onValueChange={onChangeTime}>
          <SelectTrigger className="border-0 px-0 h-7 text-sm font-semibold focus:ring-0" data-testid={`${testIdPrefix}-time`}>
            <SelectValue>{_to12h(time)}</SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {TIME_OPTIONS.map((t) => (
              <SelectItem key={t} value={t}>{_to12h(t)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
